import type { CustodyCase, NoteFile } from '../types/case';
import { seedCases } from './cases';
import { defaultConfig } from './config';
import { buildNoteBody, combinedFileNo, fileDivision } from '../utils/noteSheet';

const DLO = 'মোঃ ইমরান হোসেন';
const APR = 'জনাব কামরুল হাসান';
const t = (s: string) => new Date(s).toISOString();
const pick = (pcrs: string[]) => seedCases.filter((c) => pcrs.includes(c.pcr));

function file(id: string, seq: number, pcrs: string[], rest: Partial<NoteFile>): NoteFile {
  const cases = pick(pcrs);
  return {
    id,
    fileNo: combinedFileNo(seq),
    subject: pcrs.length > 1 ? `আটককৃত ${pcrs.length === 2 ? 'দুইটি' : 'একাধিক'} যানবাহন একত্রে অবমুক্তির অনুমোদন প্রসঙ্গে।` : 'আটককৃত যানবাহন অবমুক্তির অনুমোদন প্রসঙ্গে।',
    division: fileDivision(cases),
    pcrs,
    bodyHtml: buildNoteBody(cases, defaultConfig),
    observation: 'তফসিলভুক্ত প্রতিটি কেসের দাখিলকৃত কাগজপত্র মূল কপির সাথে মিলিয়ে দেখা হয়েছে এবং জরিমানা পরিশোধ সমন্বিত পাওয়া গেছে।',
    recommendation: 'সদয় অনুমোদনের জন্য পেশ করা হলো।',
    attachments: ['তফসিল-“ক”: অন্তর্ভুক্ত PCR তালিকা', 'প্রতিটি PCR-এর যাচাইকৃত কাগজপত্র', 'পেমেন্ট নিশ্চিতকরণ / সরকারি চালান'],
    status: 'draft',
    createdAt: t('2026-10-10T10:00:00'),
    createdBy: DLO,
    forwards: [],
    excluded: [],
    ...rest,
  };
}

export const seedNoteFiles: NoteFile[] = [
  file('NF-401', 401, ['PCR-2026-00415', 'PCR-2026-00426'], {
    status: 'approved', createdAt: t('2026-10-07T12:00:00'), savedAt: t('2026-10-08T13:50:00'),
    decidedAt: t('2026-10-09T11:00:00'), decidedBy: APR, comment: 'তফসিলভুক্ত উভয় যানবাহন অবমুক্তির চূড়ান্ত অনুমোদন প্রদান করা হলো।',
    forwards: [
      { at: t('2026-10-08T14:00:00'), from: DLO, fromRole: 'নথি প্রস্তুতকারী', to: APR, action: 'অগ্রায়ন', remark: 'সদয় অনুমোদনের জন্য পেশ করা হলো।' },
      { at: t('2026-10-09T11:00:00'), from: APR, fromRole: 'অনুমোদনকারী কর্মকর্তা', to: 'অবমুক্তি পত্র শাখা', action: 'চূড়ান্ত অনুমোদন', remark: 'তফসিলভুক্ত উভয় যানবাহন অবমুক্তির চূড়ান্ত অনুমোদন প্রদান করা হলো।' },
    ],
  }),
  file('NF-402', 402, ['PCR-2026-00424'], {
    status: 'approved', createdAt: t('2026-10-11T15:00:00'), savedAt: t('2026-10-12T09:50:00'),
    decidedAt: t('2026-10-13T12:00:00'), decidedBy: APR, comment: 'অনুমোদিত। অবমুক্তি পত্র ইস্যু করা হোক।',
    forwards: [
      { at: t('2026-10-12T10:00:00'), from: DLO, fromRole: 'নথি প্রস্তুতকারী', to: APR, action: 'অগ্রায়ন', remark: 'অনুমোদনের জন্য পেশ।' },
      { at: t('2026-10-13T12:00:00'), from: APR, fromRole: 'অনুমোদনকারী কর্মকর্তা', to: 'অবমুক্তি পত্র শাখা', action: 'চূড়ান্ত অনুমোদন', remark: 'অনুমোদিত। অবমুক্তি পত্র ইস্যু করা হোক।' },
    ],
  }),
  file('NF-403', 403, ['PCR-2026-00423', 'PCR-2026-00431'], {
    status: 'pending', createdAt: t('2026-10-14T11:00:00'), savedAt: t('2026-10-14T15:25:00'),
    forwards: [
      { at: t('2026-10-14T15:30:00'), from: DLO, fromRole: 'নথি প্রস্তুতকারী', to: APR, action: 'অগ্রায়ন', remark: 'ওয়ারী বিভাগের দুইটি কেস একত্রে সদয় অনুমোদনের জন্য পেশ করা হলো।' },
    ],
  }),
  file('NF-404', 404, ['PCR-2026-00417'], {
    status: 'returned', createdAt: t('2026-10-10T16:00:00'), savedAt: t('2026-10-11T09:50:00'),
    decidedAt: t('2026-10-12T14:00:00'), decidedBy: APR, comment: 'ট্যাক্স টোকেনের মেয়াদ সংক্রান্ত পর্যবেক্ষণ অনুচ্ছেদে উল্লেখ করে পুনরায় উপস্থাপন করুন।',
    forwards: [
      { at: t('2026-10-11T10:00:00'), from: DLO, fromRole: 'নথি প্রস্তুতকারী', to: APR, action: 'অগ্রায়ন', remark: 'অনুমোদনের জন্য পেশ।' },
      { at: t('2026-10-12T14:00:00'), from: APR, fromRole: 'অনুমোদনকারী কর্মকর্তা', to: DLO, action: 'সংশোধনের জন্য ফেরত', remark: 'ট্যাক্স টোকেনের মেয়াদ সংক্রান্ত পর্যবেক্ষণ অনুচ্ছেদে উল্লেখ করে পুনরায় উপস্থাপন করুন।' },
    ],
  }),
];

/** Links seed cases to their seeded e-files. */
export function linkNoteFiles(cases: CustodyCase[], files: NoteFile[]): CustodyCase[] {
  return cases.map((c) => {
    const f = files.find((x) => x.pcrs.includes(c.pcr));
    return f ? { ...c, noteFileId: f.id, noteFileNo: f.status === 'approved' ? f.fileNo : undefined } : c;
  });
}
